// cspell:ignore Sommel sauvignon sonoma terroir
// src/services/fallbackEnhancementService.js
/**
 * SommelAI Fallback Enhancement Service
 *
 * Provides intelligent fallback mechanisms when AI services or external APIs fail.
 * Uses static knowledge bases, local algorithms, and community data to ensure
 * the enhancement pipeline always returns useful results.
 */

// Removed unused import

// Cache for fallback enhancement results to avoid recomputation
const enhancementCache = new Map();
const CACHE_TTL = 3600000; // 1 hour cache TTL

/**
 * Helper function to create cache key for beverage
 */
function createCacheKey(beverage, category) {
  return `${category.toLowerCase()}:${beverage.name?.toLowerCase() || ""}:${
    beverage.price || ""
  }:${beverage.description?.substring(0, 50) || ""}`;
}

/**
 * Helper function to check if cached result is still valid
 */
function isCacheValid(cachedItem) {
  return cachedItem && Date.now() - cachedItem.timestamp < CACHE_TTL;
}

/**
 * Static knowledge base for beverage enhancement
 */
const BEVERAGE_KNOWLEDGE_BASE = {
  wine: {
    regions: {
      "napa valley": {
        characteristics: ["bold", "concentrated", "powerful"],
        climate: "Mediterranean",
        varieties: ["cabernet sauvignon", "chardonnay", "pinot noir"],
      },
      sonoma: {
        characteristics: ["elegant", "balanced", "terroir-driven"],
        climate: "Cool coastal influence",
        varieties: ["pinot noir", "chardonnay", "zinfandel"],
      },
      burgundy: {
        characteristics: ["elegant", "mineral", "terroir-focused"],
        climate: "Continental",
        varieties: ["pinot noir", "chardonnay"],
      },
      bordeaux: {
        characteristics: ["structured", "complex", "age-worthy"],
        climate: "Maritime",
        varieties: ["cabernet sauvignon", "merlot", "cabernet franc"],
      },
    },
    varieties: {
      "cabernet sauvignon": {
        profile: {
          body: "full",
          tannins: "high",
          acidity: "medium-high",
          alcohol: "medium-high",
        },
        flavors: ["blackcurrant", "cedar", "tobacco", "dark chocolate"],
        pairings: ["red meat", "aged cheese", "lamb", "game"],
      },
      "pinot noir": {
        profile: {
          body: "light-medium",
          tannins: "low-medium",
          acidity: "high",
          alcohol: "medium",
        },
        flavors: ["cherry", "raspberry", "earth", "mushroom"],
        pairings: ["duck", "salmon", "mushroom dishes", "soft cheese"],
      },
      chardonnay: {
        profile: {
          body: "medium-full",
          acidity: "medium-high",
          alcohol: "medium-high",
        },
        flavors: ["apple", "citrus", "butter", "vanilla"],
        pairings: ["seafood", "poultry", "cream sauces", "soft cheese"],
      },
    },
  },
  beer: {
    styles: {
      ipa: {
        profile: {
          bitterness: "high",
          alcohol: "medium-high",
          body: "medium",
          color: "golden to amber",
        },
        flavors: ["citrus", "pine", "tropical fruit", "caramel"],
        pairings: ["spicy food", "grilled meat", "aged cheese", "curry"],
      },
      stout: {
        profile: {
          bitterness: "medium",
          alcohol: "medium",
          body: "full",
          color: "dark brown to black",
        },
        flavors: ["coffee", "chocolate", "roasted malt", "cream"],
        pairings: [
          "chocolate desserts",
          "oysters",
          "grilled meat",
          "aged cheese",
        ],
      },
      pilsner: {
        profile: {
          bitterness: "medium",
          alcohol: "low-medium",
          body: "light",
          color: "pale gold",
        },
        flavors: ["crisp", "clean", "floral hops", "bread"],
        pairings: ["seafood", "salads", "light appetizers", "fresh cheese"],
      },
    },
  },
  spirits: {
    categories: {
      bourbon: {
        profile: {
          body: "full",
          sweetness: "medium-high",
          alcohol: "high",
          aging: "oak barrel",
        },
        flavors: ["vanilla", "caramel", "oak", "spice"],
        cocktails: ["old fashioned", "manhattan", "mint julep", "boulevardier"],
      },
      scotch: {
        profile: {
          body: "medium-full",
          smokiness: "varies",
          alcohol: "high",
          aging: "oak barrel",
        },
        flavors: ["honey", "smoke", "fruit", "spice"],
        cocktails: [
          "rob roy",
          "scotch and soda",
          "blood and sand",
          "rusty nail",
        ],
      },
      gin: {
        profile: {
          body: "light-medium",
          botanicals: "juniper-forward",
          alcohol: "medium-high",
        },
        flavors: ["juniper", "citrus", "herbs", "spice"],
        cocktails: ["gin and tonic", "martini", "negroni", "tom collins"],
      },
    },
  },
  cocktails: {
    classics: {
      negroni: {
        ingredients: ["gin", "campari", "sweet vermouth"],
        method: "stirred",
        glass: "rocks",
        garnish: "orange peel",
        profile: ["bitter", "herbal", "complex"],
      },
      manhattan: {
        ingredients: ["whiskey", "sweet vermouth", "angostura bitters"],
        method: "stirred",
        glass: "coupe",
        garnish: "cherry",
        profile: ["spirit-forward", "smooth", "sophisticated"],
      },
      margarita: {
        ingredients: ["tequila", "lime juice", "triple sec"],
        method: "shaken",
        glass: "coupe or rocks",
        garnish: "lime wheel",
        profile: ["citrusy", "bright", "refreshing"],
      },
    },
  },
};

/**
 * Generate fallback enhancement using static knowledge
 */
export async function generateFallbackEnhancement(beverage, category) {
  console.log(
    `🔄 Generating fallback enhancement for ${beverage.name} (${category})`
  );

  const cacheKey = createCacheKey(beverage, category);
  const cachedResult = enhancementCache.get(cacheKey);

  if (isCacheValid(cachedResult)) {
    console.log("✅ Using cached enhancement result");
    return {
      ...cachedResult,
      source: "fallback_knowledge_base",
      timestamp: new Date().toISOString(),
    };
  }

  const enhancement = {
    timestamp: new Date().toISOString(),
    source: "fallback_knowledge_base",
    confidence: 0.6, // Medium confidence for static data
    fallbackReason: "AI/External APIs unavailable",
  };

  try {
    switch (category.toLowerCase()) {
      case "wine":
        enhancement.data = await generateWineFallback(beverage);
        break;
      case "beer":
        enhancement.data = await generateBeerFallback(beverage);
        break;
      case "spirit":
      case "spirits":
        enhancement.data = await generateSpiritFallback(beverage);
        break;
      case "cocktail":
        enhancement.data = await generateCocktailFallback(beverage);
        break;
      default:
        enhancement.data = await generateGenericFallback(beverage, category);
    }

    enhancement.confidence = calculateEnhancementConfidence(enhancement.data);
    console.log(
      `✅ Fallback enhancement complete: ${
        enhancement.confidence * 100
      }% confidence`
    );

    // Cache the result
    enhancementCache.set(cacheKey, {
      ...enhancement,
      timestamp: Date.now(),
    });

    return enhancement;
  } catch (error) {
    console.error("Fallback enhancement error:", error);
    return {
      ...enhancement,
      confidence: 0.2,
      data: generateMinimalFallback(beverage, category),
      error: error.message,
    };
  }
}

/**
 * Generate wine-specific fallback data
 */
async function generateWineFallback(beverage) {
  const name = beverage.name.toLowerCase();
  const data = {
    category: "wine",
    tastingProfile: {},
    foodPairings: [],
    producer: {},
    characteristics: [],
  };

  // Detect variety from name
  for (const [variety, info] of Object.entries(
    BEVERAGE_KNOWLEDGE_BASE.wine.varieties
  )) {
    if (name.includes(variety)) {
      data.variety = variety;
      data.tastingProfile = {
        ...info.profile,
        flavors: info.flavors,
        confidence: "moderate",
      };
      data.foodPairings = info.pairings.map((pairing) => ({
        food: pairing,
        reason: `Classic pairing for ${variety}`,
        confidence: "good",
      }));
      data.characteristics = info.flavors;
      break;
    }
  }

  // Detect region from name
  for (const [region, info] of Object.entries(
    BEVERAGE_KNOWLEDGE_BASE.wine.regions
  )) {
    if (name.includes(region.replace(" ", ""))) {
      data.region = region;
      data.producer.region = region;
      data.producer.characteristics = info.characteristics;
      data.producer.climate = info.climate;
      break;
    }
  }

  // Extract vintage if present
  const vintageMatch = name.match(/\b(19|20)\d{2}\b/);
  if (vintageMatch) {
    data.vintage = parseInt(vintageMatch[0]);
    data.ageAnalysis = generateAgeAnalysis(data.vintage);
  }

  return data;
}

/**
 * Generate beer-specific fallback data
 */
async function generateBeerFallback(beverage) {
  const name = beverage.name.toLowerCase();
  const data = {
    category: "beer",
    style: {},
    brewingProfile: {},
    tastingNotes: {},
    foodPairings: [],
  };

  // Detect style from name
  for (const [style, info] of Object.entries(
    BEVERAGE_KNOWLEDGE_BASE.beer.styles
  )) {
    if (name.includes(style) || name.includes(style.replace(/\s+/g, ""))) {
      data.style.name = style;
      data.style.profile = info.profile;
      data.tastingNotes = {
        flavors: info.flavors,
        profile: info.profile,
        confidence: "moderate",
      };
      data.foodPairings = info.pairings.map((pairing) => ({
        food: pairing,
        reason: `Traditional pairing for ${style}`,
        confidence: "good",
      }));
      break;
    }
  }

  // Common beer descriptors
  if (name.includes("imperial") || name.includes("double")) {
    data.style.strength = "high";
    data.brewingProfile.alcoholContent = "high";
  }

  if (name.includes("session")) {
    data.style.strength = "low";
    data.brewingProfile.alcoholContent = "low";
  }

  return data;
}

/**
 * Generate spirit-specific fallback data
 */
async function generateSpiritFallback(beverage) {
  const name = beverage.name.toLowerCase();
  const data = {
    category: "spirit",
    spiritType: {},
    tastingProfile: {},
    cocktailRecommendations: [],
    characteristics: [],
  };

  // Detect spirit type from name
  for (const [spiritType, info] of Object.entries(
    BEVERAGE_KNOWLEDGE_BASE.spirits.categories
  )) {
    if (name.includes(spiritType)) {
      data.spiritType.name = spiritType;
      data.spiritType.profile = info.profile;
      data.tastingProfile = {
        ...info.profile,
        flavors: info.flavors,
        confidence: "moderate",
      };
      data.cocktailRecommendations = info.cocktails.map((cocktail) => ({
        name: cocktail,
        reason: `Classic ${spiritType} cocktail`,
        difficulty: "medium",
      }));
      data.characteristics = info.flavors;
      break;
    }
  }

  // Age statements
  const ageMatch = name.match(/(\d+)\s*year/i);
  if (ageMatch) {
    data.age = parseInt(ageMatch[1]);
    data.ageAnalysis = generateAgeAnalysis(null, data.age);
  }

  return data;
}

/**
 * Generate cocktail-specific fallback data
 */
async function generateCocktailFallback(beverage) {
  const name = beverage.name.toLowerCase();
  const data = {
    category: "cocktail",
    recipe: {},
    technique: {},
    variations: [],
    history: {},
  };

  // Check for classic cocktails
  for (const [cocktailName, info] of Object.entries(
    BEVERAGE_KNOWLEDGE_BASE.cocktails.classics
  )) {
    if (name.includes(cocktailName)) {
      data.recipe = {
        ingredients: info.ingredients,
        method: info.method,
        glass: info.glass,
        garnish: info.garnish,
      };
      data.technique = {
        method: info.method,
        difficulty: "medium",
        time: "2-3 minutes",
      };
      data.profile = info.profile;

      // Generate simple variations
      data.variations = generateCocktailVariations(cocktailName, info);
      break;
    }
  }

  return data;
}

/**
 * Generate generic fallback for unknown categories
 */
async function generateGenericFallback(beverage, category) {
  return {
    category: category,
    basicInfo: {
      name: beverage.name,
      type: category,
      description: `A ${category} beverage`,
    },
    characteristics: extractCharacteristicsFromName(beverage.name),
    confidence: "low",
  };
}

/**
 * Generate minimal fallback when all else fails
 */
function generateMinimalFallback(beverage, category) {
  return {
    category: category,
    name: beverage.name,
    basicEnhancement: {
      timestamp: new Date().toISOString(),
      source: "minimal_fallback",
      description: `Basic information for ${beverage.name}`,
      confidence: "very_low",
    },
  };
}

/**
 * Calculate confidence score for fallback enhancement
 */
function calculateEnhancementConfidence(data) {
  let score = 0.2; // Base score for fallback

  if (data.tastingProfile && Object.keys(data.tastingProfile).length > 0)
    score += 0.2;
  if (data.foodPairings && data.foodPairings.length > 0) score += 0.1;
  if (data.characteristics && data.characteristics.length > 0) score += 0.1;
  if (data.recipe || data.style || data.spiritType) score += 0.2;
  if (data.region || data.producer || data.brewery) score += 0.1;
  if (data.vintage || data.age) score += 0.1;

  return Math.min(score, 0.8); // Cap at 80% for fallback data
}

/**
 * Generate age analysis for wines and spirits
 */
function generateAgeAnalysis(vintage, age) {
  const currentYear = new Date().getFullYear();
  const analysis = { confidence: "moderate" };

  if (vintage) {
    const yearsOld = currentYear - vintage;
    analysis.age = yearsOld;

    if (yearsOld <= 3) {
      analysis.maturity = "young";
      analysis.drinkingWindow = "now to 5 years";
    } else if (yearsOld <= 10) {
      analysis.maturity = "developing";
      analysis.drinkingWindow = "now to 10 years";
    } else {
      analysis.maturity = "mature";
      analysis.drinkingWindow = "drink now";
    }
  }

  if (age) {
    analysis.ageStatement = `${age} years`;

    if (age <= 8) {
      analysis.character = "youthful with bright flavors";
    } else if (age <= 15) {
      analysis.character = "balanced with complexity";
    } else {
      analysis.character = "mature with deep complexity";
    }
  }

  return analysis;
}

/**
 * Extract characteristics from beverage name
 */
function extractCharacteristicsFromName(name) {
  const characteristics = [];
  const lowerName = name.toLowerCase();

  const descriptors = {
    reserve: "premium quality",
    estate: "estate-grown",
    single: "single origin",
    aged: "aged",
    barrel: "barrel-aged",
    vintage: "vintage",
    limited: "limited edition",
    special: "special release",
    craft: "artisanal",
    "small batch": "small batch",
    organic: "organic",
    biodynamic: "biodynamic",
  };

  for (const [keyword, characteristic] of Object.entries(descriptors)) {
    if (lowerName.includes(keyword)) {
      characteristics.push(characteristic);
    }
  }

  return characteristics;
}

/**
 * Generate simple cocktail variations
 */
function generateCocktailVariations(baseName, baseInfo) {
  const variations = [];

  // Common variation patterns
  if (baseInfo.ingredients.includes("gin") && baseName !== "negroni") {
    variations.push({
      name: `${baseName} Royale`,
      modification: "Add champagne float",
      difficulty: "easy",
    });
  }

  if (
    baseInfo.ingredients.includes("whiskey") ||
    baseInfo.ingredients.includes("bourbon")
  ) {
    variations.push({
      name: `Smoked ${baseName}`,
      modification: "Add smoky garnish",
      difficulty: "medium",
    });
  }

  variations.push({
    name: `${baseName} Twist`,
    modification: "Substitute premium base spirit",
    difficulty: "easy",
  });

  return variations.slice(0, 3); // Limit to 3 variations
}

/**
 * Enhance external API data with fallback information
 */
export async function enhanceWithFallbackData(
  originalData,
  beverage,
  category
) {
  if (!originalData || Object.keys(originalData).length === 0) {
    return generateFallbackEnhancement(beverage, category);
  }

  // Fill in gaps in existing data
  const fallbackData = await generateFallbackEnhancement(beverage, category);

  return {
    ...originalData,
    fallbackEnhancements: fallbackData.data,
    confidence: Math.max(originalData.confidence || 0, fallbackData.confidence),
    sources: [...(originalData.sources || []), "fallback_knowledge_base"],
  };
}

// Class wrapper for service orchestration compatibility
export class FallbackEnhancementService {
  async generateEnhancement(beverage, category) {
    return generateFallbackEnhancement(beverage, category);
  }

  async enhanceWithFallback(originalData, beverage, category) {
    return enhanceWithFallbackData(originalData, beverage, category);
  }
}

const fallbackService = {
  generateFallbackEnhancement,
  enhanceWithFallbackData,
  BEVERAGE_KNOWLEDGE_BASE,
};

export default fallbackService;
